/* =====================================================================
 * SelectDropdown — 공통 셀렉트 드롭다운 컴포넌트 (테마 기반)
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Check, ChevronDown } from 'lucide-react';
import { Input } from './Input';

export interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  description?: string;
}

type SelectTone = 'default' | 'accent';

interface SelectDropdownProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  tone?: SelectTone;
  placement?: 'bottom' | 'top';
  className?: string;
}

export const SelectDropdown: React.FC<SelectDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = '선택하세요',
  disabled = false,
  size = 'md',
  searchable = false,
  searchPlaceholder = '검색',
  emptyMessage = '선택 가능한 항목이 없습니다.',
  tone = 'default',
  placement = 'bottom',
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value);
  const visibleOptions = useMemo(() => {
    if (!searchable) return options;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => {
      const label = opt.label.toLowerCase();
      const description = (opt.description ?? '').toLowerCase();
      const optionValue = opt.value.toLowerCase();
      return label.includes(q) || description.includes(q) || optionValue.includes(q);
    });
  }, [options, searchable, searchQuery]);

  const close = useCallback(() => {
    setOpen(false);
    setHighlightIdx(-1);
    setSearchQuery('');
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open, close]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!open) {
            setOpen(true);
            const idx = visibleOptions.findIndex((o) => o.value === value);
            setHighlightIdx(idx >= 0 ? idx : 0);
          } else if (highlightIdx >= 0 && highlightIdx < visibleOptions.length) {
            onChange(visibleOptions[highlightIdx].value);
            close();
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (!open) {
            setOpen(true);
            const idx = visibleOptions.findIndex((o) => o.value === value);
            setHighlightIdx(idx >= 0 ? idx : 0);
          } else {
            setHighlightIdx((prev) => (prev < visibleOptions.length - 1 ? prev + 1 : 0));
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (open) {
            setHighlightIdx((prev) => (prev > 0 ? prev - 1 : visibleOptions.length - 1));
          }
          break;
        case 'Escape':
          e.preventDefault();
          close();
          break;
      }
    },
    [open, highlightIdx, visibleOptions, value, onChange, close, disabled],
  );

  useEffect(() => {
    if (!open || highlightIdx < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('[data-option]');
    items[highlightIdx]?.scrollIntoView({ block: 'nearest' });
  }, [open, highlightIdx]);

  useEffect(() => {
    if (!open) return;
    const idx = visibleOptions.findIndex((o) => o.value === value);
    if (idx >= 0) {
      setHighlightIdx(idx);
      return;
    }
    setHighlightIdx(visibleOptions.length > 0 ? 0 : -1);
  }, [open, value, visibleOptions]);

  useEffect(() => {
    if (!open || !searchable) return;
    const timeoutId = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [open, searchable]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightIdx((prev) => (prev < visibleOptions.length - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightIdx((prev) => (prev > 0 ? prev - 1 : visibleOptions.length - 1));
          break;
        case 'Enter':
          if (highlightIdx >= 0 && highlightIdx < visibleOptions.length) {
            e.preventDefault();
            onChange(visibleOptions[highlightIdx].value);
            close();
          }
          break;
        case 'Escape':
          e.preventDefault();
          close();
          break;
      }
    },
    [highlightIdx, visibleOptions, onChange, close],
  );

  return (
    <Container
      ref={containerRef}
      className={className}
      data-open={open ? 'true' : 'false'}
    >
      <Trigger
        type="button"
        $open={open}
        $size={size}
        $hasValue={!!selected}
        $tone={tone}
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((p) => !p);
        }}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <TriggerContent>
          {selected?.icon && <OptionIcon>{selected.icon}</OptionIcon>}
          <TriggerLabel $placeholder={!selected} data-select-trigger-label="true">
            {selected ? selected.label : placeholder}
          </TriggerLabel>
        </TriggerContent>
        <ChevronWrap $open={open} data-select-chevron="true">
          <ChevronDown size={15} />
        </ChevronWrap>
      </Trigger>

      {open && (
        <ListPanel
          ref={listRef}
          $size={size}
          $tone={tone}
          $placement={placement}
          role="listbox"
          data-placement={placement}
        >
          {searchable && (
            <SearchWrap>
              <Input
                ref={searchInputRef}
                variant="search"
                inputSize="sm"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
            </SearchWrap>
          )}
          {visibleOptions.map((opt, idx) => {
            const isSelected = opt.value === value;
            const isHighlighted = idx === highlightIdx;
            return (
              <OptionItem
                key={opt.value}
                data-option
                role="option"
                aria-selected={isSelected}
                $selected={isSelected}
                $highlighted={isHighlighted}
                $tone={tone}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange(opt.value);
                  close();
                }}
                onMouseEnter={() => setHighlightIdx(idx)}
              >
                <OptionLeft>
                  {opt.icon && <OptionIcon>{opt.icon}</OptionIcon>}
                  <OptionTexts>
                    <OptionLabel data-select-option-label="true">{opt.label}</OptionLabel>
                    {opt.description && <OptionDesc data-select-option-desc="true">{opt.description}</OptionDesc>}
                  </OptionTexts>
                </OptionLeft>
                {isSelected && (
                  <CheckIcon $tone={tone}>
                    <Check size={14} strokeWidth={2.5} />
                  </CheckIcon>
                )}
              </OptionItem>
            );
          })}
          {visibleOptions.length === 0 && <EmptyMsg>{emptyMessage}</EmptyMsg>}
        </ListPanel>
      )}
    </Container>
  );
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const panelSlide = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  position: relative;
  width: 100%;

  &[data-open='true'] {
    z-index: 120;
  }
`;

const Trigger = styled.button<{
  $open: boolean;
  $size: 'sm' | 'md';
  $hasValue: boolean;
  $tone: SelectTone;
}>`
  position: relative;
  overflow: hidden;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border: 1px solid ${({ theme, $open, $tone }) =>
    $open
      ? (
        $tone === 'accent'
          ? (theme.mode === 'dark' ? 'rgba(255,138,0,0.24)' : 'rgba(255,107,0,0.24)')
          : (theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)')
      )
      : (theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')};
  border-radius: 10px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.7)'};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: ${({ theme }) => theme.fonts.sans};
  cursor: pointer;
  transition: all 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(135deg, #FF6B00, #FFA800);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: ${({ $tone, $open }) => ($tone === 'accent' && $open ? 1 : 0)};
    transition: opacity 0.25s ease;
    pointer-events: none;
  }

  ${({ $size }) =>
    $size === 'sm'
      ? css`
          padding: 6px 10px;
          font-size: 0.8125rem;
          min-height: 34px;
        `
      : css`
          padding: 9px 12px;
          font-size: 0.85rem;
          min-height: 40px;
        `}

  &:hover:not(:disabled) {
    border-color: ${({ theme, $tone }) =>
      $tone === 'accent'
        ? (theme.mode === 'dark' ? 'rgba(255,138,0,0.22)' : 'rgba(255,107,0,0.22)')
        : (theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)')};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#ffffff'};
    box-shadow: ${({ $tone }) =>
      $tone === 'accent' ? '0 8px 24px rgba(255, 107, 0, 0.12)' : 'none'};
    transform: ${({ $tone }) => ($tone === 'accent' ? 'translateY(-1px)' : 'none')};
  }

  &:focus-visible {
    outline: none;
    border-color: ${({ theme, $tone }) =>
      $tone === 'accent'
        ? '#FF8A00'
        : (theme.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)')};
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 0 0 2px rgba(255,255,255,0.06)'
        : '0 0 0 2px rgba(0,0,0,0.04)'};
    ${({ $tone }) =>
      $tone === 'accent' &&
      css`
        box-shadow:
          0 0 0 2px rgba(255, 138, 0, 0.14),
          0 10px 26px rgba(255, 107, 0, 0.12);
      `}
  }

  &:hover:not(:disabled)::before {
    opacity: ${({ $tone }) => ($tone === 'accent' ? 1 : 0)};
  }

  &:active:not(:disabled) {
    transform: scale(0.985);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TriggerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const TriggerLabel = styled.span<{ $placeholder: boolean }>`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ theme, $placeholder }) =>
    $placeholder ? theme.colors.textMuted : theme.colors.textPrimary};
`;

const ChevronWrap = styled.span<{ $open: boolean }>`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: transform ${({ theme }) => theme.transitions.fast};
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  flex-shrink: 0;
`;

const ListPanel = styled.div<{
  $size: 'sm' | 'md';
  $tone: SelectTone;
  $placement: 'bottom' | 'top';
}>`
  position: absolute;
  top: ${({ $placement }) => ($placement === 'top' ? 'auto' : 'calc(100% + 4px)')};
  bottom: ${({ $placement }) => ($placement === 'top' ? 'calc(100% + 4px)' : 'auto')};
  left: 0;
  right: 0;
  z-index: 50;
  max-height: 240px;
  overflow-y: auto;
  padding: 4px;
  border: 1px solid ${({ theme, $tone }) =>
    $tone === 'accent'
      ? (theme.mode === 'dark' ? 'rgba(255,138,0,0.18)' : 'rgba(255,107,0,0.16)')
      : (theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')};
  border-radius: 12px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(40,40,42,0.92)' : 'rgba(255,255,255,0.92)'};
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.45), 0 1px 3px rgba(0,0,0,0.3)'
      : '0 8px 32px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.06)'};
  animation: ${panelSlide} 0.15s ease;
  transform-origin: ${({ $placement }) => ($placement === 'top' ? 'bottom center' : 'top center')};

  ${({ $tone }) =>
    $tone === 'accent' &&
    css`
      box-shadow:
        0 10px 32px rgba(255, 107, 0, 0.12),
        0 8px 32px rgba(0,0,0,0.1),
        0 1px 3px rgba(0,0,0,0.06);
    `}

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.scrollbar};
    border-radius: 3px;
  }
`;

const SearchWrap = styled.div`
  padding: 4px 4px 8px;
`;

const OptionItem = styled.div<{ $selected: boolean; $highlighted: boolean; $tone: SelectTone }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 36px;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.18s ease, box-shadow 0.18s ease;

  background: ${({ theme, $highlighted, $selected, $tone }) => {
    if ($tone === 'accent') {
      if ($highlighted) {
        return theme.mode === 'dark' ? 'rgba(255, 138, 0, 0.14)' : 'rgba(255, 138, 0, 0.1)';
      }
      if ($selected) {
        return theme.mode === 'dark' ? 'rgba(255, 107, 0, 0.12)' : 'rgba(255, 107, 0, 0.08)';
      }
      return 'transparent';
    }
    return $highlighted
      ? (theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)')
      : $selected
        ? (theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)')
        : 'transparent';
  }};

  ${({ $tone, $highlighted, $selected }) =>
    $tone === 'accent' &&
    ($highlighted || $selected) &&
    css`
      box-shadow: inset 0 0 0 1px rgba(255, 138, 0, 0.16);
    `}

  &:hover {
    ${({ $tone }) =>
      $tone === 'accent' &&
      css`
        transform: translateX(2px);
      `}
  }

  &:active {
    background: ${({ theme, $tone }) =>
      $tone === 'accent'
        ? (theme.mode === 'dark' ? 'rgba(255, 138, 0, 0.16)' : 'rgba(255, 138, 0, 0.12)')
        : (theme.mode === 'dark' ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.06)')};
  }
`;

const OptionLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const OptionIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const OptionTexts = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const OptionLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const OptionDesc = styled.span`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 1px;
`;

const CheckIcon = styled.span<{ $tone: SelectTone }>`
  display: flex;
  align-items: center;
  color: ${({ theme, $tone }) => ($tone === 'accent' ? '#FF8A00' : theme.colors.accent)};
  flex-shrink: 0;
`;

const EmptyMsg = styled.div`
  padding: 12px;
  text-align: center;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;
